import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { isMaskedSecret } from './integrationHelpers';

interface SecretFieldProps {
    id?: string;
    name?: string;
    /** Current form value: the stored mask while untouched, what the user typed after Replace. */
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    /** The masked value the API returned (`••••1234`), or empty when nothing is stored. */
    saved?: string | null;
    disabled?: boolean;
    invalid?: boolean;
    describedBy?: string;
    placeholder?: string;
}

/**
 * A secret input. A stored secret is shown masked and read-only with a Replace button;
 * only a replaced value is ever sent back (see `toConfiguration`).
 * Mount it with `key={saved}` so a refetch after saving returns it to the masked state.
 */
export const SecretField: React.FC<SecretFieldProps> = ({
    id,
    name,
    value,
    onChange,
    onBlur,
    saved,
    disabled,
    invalid,
    describedBy,
    placeholder,
}) => {
    const { t } = useTranslation('crm');
    const hasSaved = isMaskedSecret(saved);
    const [replacing, setReplacing] = useState(!hasSaved);

    if (hasSaved && !replacing) {
        return (
            <div className="flex items-center gap-2">
                <Input
                    id={id}
                    name={name}
                    value={saved ?? ''}
                    readOnly
                    aria-readonly="true"
                    className="font-mono text-muted-foreground"
                    aria-describedby={describedBy}
                />
                {!disabled && (
                    <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            onChange('');
                            setReplacing(true);
                        }}
                    >
                        {t('integrations.form.replace')}
                    </Button>
                )}
            </div>
        );
    }

    return (
        <div className="flex items-center gap-2">
            <Input
                id={id}
                name={name}
                type="password"
                autoComplete="new-password"
                value={isMaskedSecret(value) ? '' : value}
                onChange={event => onChange(event.target.value)}
                onBlur={onBlur}
                disabled={disabled}
                aria-invalid={invalid}
                aria-describedby={describedBy}
                placeholder={hasSaved ? t('integrations.form.newValue') : placeholder}
                autoFocus={hasSaved}
            />
            {hasSaved && (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        onChange(saved ?? '');
                        setReplacing(false);
                    }}
                >
                    {t('actions.cancel')}
                </Button>
            )}
        </div>
    );
};

export default SecretField;
